import React, { useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import { useCrossFlow, Journeys, Targets } from '@atlassiansox/cross-flow-support';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { CompassLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import { Box, Grid } from '@atlaskit/primitives';
import { cssMap, cx } from '@atlaskit/css';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { useIsStagingOrDevOrLocal } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { uiConnectedRepositoryListQuery } from '@atlassian/jira-relay/src/__generated__/uiConnectedRepositoryListQuery.graphql';
import { CrossflowImage } from '../assets/index.tsx';
import { COMPASS_PRODUCT_LINK } from '../common/constants.tsx';
import { hasCompassBeenProvisioned } from '../services/license-information/index.tsx';
import { ConnectedRepositoryList } from './connected-repository-list/index.tsx';
import AutoCatalogEmptyStateLeftPanel from './auto-catalog-empt-state-left-panel/index.tsx';
import messages from './messages.tsx';
import type { Props, Repository, TouchPointProps } from './types.tsx';

const NewCrossFlowTouchPoint = (props: TouchPointProps) => {
	const { formatMessage } = useIntl();
	const { enabled, onTry, onLearnMore } = props;
	if (enabled) {
		return (
			<ContentWrapper data-testid="compass-cross-flow.ui.empty-state-not-provisioned">
				<Box>
					<CompassLogo appearance="brand" size="medium" />
					<TouchPointMessageWrapper>
						<Heading size="large" as="h2">
							{formatMessage(messages.headLine)}
						</Heading>
					</TouchPointMessageWrapper>
					<TouchPointMessageDescriptionWrapper>
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.callToActionIssueTermRefresh
								: messages.callToAction,
						)}
					</TouchPointMessageDescriptionWrapper>
					<ButtonGroup>
						<Button appearance="primary" onClick={onTry}>
							{formatMessage(messages.engageMessage)}
						</Button>
						<Button
							appearance="link"
							onClick={onLearnMore}
							href={COMPASS_PRODUCT_LINK}
							target="_blank"
						>
							{formatMessage(messages.learnMoreMessage)}
						</Button>
					</ButtonGroup>
				</Box>
				<CrossflowImage />
			</ContentWrapper>
		);
	}
	return null;
};

/**
 * Basic crossflow touch point that shows a static message and a button to try Compass.
 */
const DefaultCrossFlowTouchPoint = (props: Props) => {
	const { origin, eventSource, onCrossFlowComplete } = props;
	const crossFlow = useCrossFlow();
	const { cloudId } = useTenantContext();
	const { fireInteractedUIEvent, fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

	useEffect(() => {
		if (!crossFlow.isEnabled) {
			fireErrorAnalytics({
				error: new Error(crossFlow.reason),
				meta: {
					id: crossFlow.reason,
					packageName: 'jiraCompassCrossFlow',
				},
			});
		} else {
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'section',
					actionSubjectId: 'tryCompassBanner',
				},
				{
					recommendedProductIds: [Targets.COMPASS],
					source: eventSource,
				},
			);
		}
	}, [crossFlow, eventSource, fireViewedUIEvent]);

	const onTry = useCallback(
		async (event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'tryCompassButton',
				},
				{
					targetProductId: Targets.COMPASS,
					recommendedProductIds: [Targets.COMPASS],
					source: 'tryCompassBannerSection',
				},
			);
			fireUIAnalytics(analyticsEvent, 'compassCrossFlowTouchPointTry');
			if (crossFlow.isEnabled) {
				await crossFlow.api.open({
					journey: Journeys.GET_STARTED,
					targetProduct: Targets.COMPASS,
					sourceComponent: 'jira-compass-cross-flow-try',
					sourceContext: 'jira',
				});

				// Refresh the Compass Crossflow Touchpoint once Compass is provisioned
				const result = await hasCompassBeenProvisioned(cloudId);
				if (result) {
					fireTrackAnalytics(analyticsEvent, 'compassCrossflow expandComplete', {
						hasCompassProvisioned: result,
						origin,
					});
					onCrossFlowComplete?.();
				} else {
					fireTrackAnalytics(analyticsEvent, 'compassCrossflow expandIncomplete', {
						hasCompassProvisioned: result,
						origin,
					});
				}
			}
		},
		[cloudId, crossFlow, origin, fireInteractedUIEvent, onCrossFlowComplete],
	);

	const onLearnMore = useCallback(
		async (event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'learnMoreCompassButton',
				},
				{
					targetProductId: Targets.COMPASS,
					recommendedProductIds: [Targets.COMPASS],
					source: 'tryCompassBannerSection',
				},
			);
			fireUIAnalytics(analyticsEvent, 'compassCrossFlowTouchPointLearnMore');
		},
		[fireInteractedUIEvent],
	);

	return (
		<JSErrorBoundary
			teamName="compass-lodestone"
			id="crossFlowTouchPoint"
			packageName="jiraCompassCrossFlowTouchPoint"
			sendToPrivacyUnsafeSplunk
		>
			<FireUiAnalytics action="viewed" actionSubject="jiraCompassCrossFlowTouchPoint" />
			{NewCrossFlowTouchPoint({
				enabled: crossFlow.isEnabled,
				onTry,
				onLearnMore,
			})}
		</JSErrorBoundary>
	);
};

/**
 * This crossflow touch point advertises Compass capabilities using repository data associated with current Jira project pulled from Team Work Graph.
 */
const AutoCatalogViaTeamWorkGraphCrossFlowTouchPoint = (props: Props) => {
	const { origin, eventSource, onCrossFlowComplete } = props;
	const crossFlow = useCrossFlow();
	const { fireInteractedUIEvent, fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const { cloudId } = useTenantContext();
	const isStagingOrDev = useIsStagingOrDevOrLocal();
	const providerId = isStagingOrDev
		? 'com.github.integration.staging'
		: 'com.github.integration.production';
	const projectContext = useProjectContext();
	const projectId = projectContext.data?.projectId;
	if (!projectId) {
		throw new Error('Provisioning did not return projectId');
	}
	const jiraProjectAri = JiraProjectAri.create({
		siteId: cloudId,
		projectId: projectId.toString(),
	}).toString();

	const data = useLazyLoadQuery<uiConnectedRepositoryListQuery>(
		graphql`
			query uiConnectedRepositoryListQuery($projectId: ID!, $first: Int!) {
				graphStore @optIn(to: "GraphStore") {
					projectAssociatedRepo(id: $projectId, first: $first)
						@optIn(to: "GraphStoreProjectAssociatedRepo") {
						edges {
							node {
								... on DevOpsRepository {
									name
									providerId
									__typename
								}
							}
						}
					}
				}
			}
		`,
		{
			projectId: jiraProjectAri,
			first: 20,
		},
	);

	const repositories =
		data?.graphStore?.projectAssociatedRepo?.edges
			?.map((edge) => edge?.node)
			?.filter(
				(repo): repo is { __typename: 'DevOpsRepository'; name: string; providerId: string } =>
					repo?.__typename === 'DevOpsRepository',
			) || [];

	const filteredRepositories: Repository[] = providerId
		? repositories.filter(
				(repo) =>
					repo?.__typename === 'DevOpsRepository' &&
					repo?.providerId === providerId &&
					repo?.name !== null,
			)
		: repositories;

	useEffect(() => {
		if (!crossFlow.isEnabled) {
			fireErrorAnalytics({
				error: new Error(crossFlow.reason),
				meta: {
					id: crossFlow.reason,
					packageName: 'jiraCompassCrossFlow',
				},
			});
		} else {
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'section',
					actionSubjectId: 'tryCompassBanner',
				},
				{
					recommendedProductIds: [Targets.COMPASS],
					source: eventSource,
				},
				{
					autoCatalogingViaTwg: true,
				},
			);
		}
	}, [crossFlow, eventSource, fireViewedUIEvent]);

	const onTry = useCallback(
		async (_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'tryCompassButton',
				},
				{
					targetProductId: Targets.COMPASS,
					recommendedProductIds: [Targets.COMPASS],
					source: 'tryCompassBannerSection',
				},
				{
					autoCatalogingViaTwg: true,
				},
			);
			fireUIAnalytics(analyticsEvent, 'compassCrossFlowTouchPointTry');
			if (crossFlow.isEnabled) {
				await crossFlow.api.open({
					journey: Journeys.GET_STARTED,
					targetProduct: Targets.COMPASS,
					sourceComponent: 'jira-compass-cross-flow-try',
					sourceContext: 'jira',
					extraAnalyticsAttributes: {
						autoCatalogingViaTwg: true,
					},
				});

				// Refresh the Compass Crossflow Touchpoint once Compass is provisioned
				const result = await hasCompassBeenProvisioned(cloudId);
				if (result) {
					fireTrackAnalytics(analyticsEvent, 'compassCrossflow expandComplete', {
						hasCompassProvisioned: result,
						origin,
						autoCatalogingViaTwg: true,
					});
					onCrossFlowComplete?.();
				} else {
					fireTrackAnalytics(analyticsEvent, 'compassCrossflow expandIncomplete', {
						hasCompassProvisioned: result,
						origin,
						autoCatalogingViaTwg: true,
					});
				}
			}
		},
		[cloudId, crossFlow, origin, fireInteractedUIEvent, onCrossFlowComplete],
	);

	if (filteredRepositories.length <= 0) {
		return <DefaultCrossFlowTouchPoint {...props} />;
	}

	return (
		<Box
			testId="compass-cross-flow.ui.new-empty-state-not-provisioned"
			paddingBlock="space.400"
			paddingBlockStart="space.100"
			xcss={cx(containerStyle.root)}
		>
			<Grid templateColumns="1fr 2fr" gap="space.400" alignContent="start">
				<AutoCatalogEmptyStateLeftPanel onTry={onTry} />
				<ConnectedRepositoryList filteredRepositories={filteredRepositories} />
			</Grid>
		</Box>
	);
};

const CrossFlowTouchPoint = (props: Props) => {
	if (fg('compass_software_collections_empty_state')) {
		return <AutoCatalogViaTeamWorkGraphCrossFlowTouchPoint {...props} />;
	}

	return <DefaultCrossFlowTouchPoint {...props} />;
};
export default CrossFlowTouchPoint;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	width: '920px',
	color: token('color.text.subtle'),
	marginTop: token('space.1000'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TouchPointMessageWrapper = styled.div({
	fontWeight: token('font.weight.medium'),
	paddingTop: token('space.250'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TouchPointMessageDescriptionWrapper = styled.div({
	font: token('font.body'),
	paddingTop: token('space.250'),
	paddingRight: '0px',
	paddingBottom: token('space.250'),
	paddingLeft: '0px',
});

const containerStyle = cssMap({
	root: {
		maxWidth: '1200px',
	},
});
